/* eslint-disable react/no-unescaped-entities */
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Logo from "@/components/Logo";
import LangToggle from "@/components/LangToggle";
import { useT } from "@/i18n";
import { api } from "@/lib/api";
import useSEO from "@/hooks/useSEO";

/**
 * Terms of Service page — public, no auth required, 6 languages.
 * Prices are fetched from /api/billing/plans-public so a change in the
 * backend PLANS dict updates checkout, Pricing page, Terms page, and
 * Dashboard cards atomically.
 *
 * IMPORTANT REFUND CLAUSE: refunds are explicitly NOT automatic.
 * Each request is reviewed on a case-by-case basis. This protects us from
 * users who consume heavy paid features (Sora 2 video, voice TTS) and then
 * demand a refund — those API calls cost real money to us.
 */
export default function Terms() {
  const { lang } = useT();
  const [prices, setPrices] = useState(null);
  useSEO({
    title: "Terms of Service — Lumioos",
    description: "Lumioos terms of use: acceptable use, billing, refunds, intellectual property ownership of AI-generated content, plan changes and account termination.",
    canonical: "https://mylumioos.com/terms",
  });

  useEffect(() => {
    api.get("/billing/plans-public").then(r => setPrices(extract(r.data))).catch(() => {
      // Backend public endpoint not reachable — fall back to safe defaults
      // matching the current server.py PLANS dict so the page still renders.
      setPrices({ cur: "$", creator: { m: 5, y: 50 }, pro: { m: 10, y: 100 } });
    });
  }, []);

  const labels = {
    en: { eyebrow: "Legal", title: "Terms of Service", updated: "Last updated", privacy: "Privacy Policy", home: "Home" },
    tr: { eyebrow: "Yasal", title: "Kullanım Koşulları", updated: "Son güncelleme", privacy: "Gizlilik Politikası", home: "Ana sayfa" },
    es: { eyebrow: "Legal", title: "Términos del Servicio", updated: "Última actualización", privacy: "Política de Privacidad", home: "Inicio" },
    fr: { eyebrow: "Mentions légales", title: "Conditions d'Utilisation", updated: "Dernière mise à jour", privacy: "Politique de Confidentialité", home: "Accueil" },
    de: { eyebrow: "Rechtliches", title: "Nutzungsbedingungen", updated: "Zuletzt aktualisiert", privacy: "Datenschutzerklärung", home: "Startseite" },
    it: { eyebrow: "Legale", title: "Termini di Servizio", updated: "Ultimo aggiornamento", privacy: "Informativa sulla Privacy", home: "Home" },
  };
  const L = labels[lang] || labels.en;
  const Body = TERMS_BODIES[lang] || TERMS_BODIES.en;
  if (!prices) return <div className="min-h-screen flex items-center justify-center text-zinc-500">…</div>;
  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">
      <header className="flex items-center justify-between px-6 sm:px-12 py-6 border-b border-zinc-200">
        <Link to="/"><Logo size="md" /></Link>
        <LangToggle variant="outline" />
      </header>
      <main className="max-w-3xl mx-auto px-6 sm:px-12 py-12 prose prose-zinc">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">{L.eyebrow}</div>
        <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tighter">{L.title}</h1>
        <p className="text-sm text-zinc-500 mt-2">{L.updated}: 06.02.2026</p>

        <Body p={prices} />

        <div className="mt-12 pt-6 border-t border-zinc-200 text-sm text-zinc-500">
          <Link to="/privacy" className="underline hover:text-zinc-900">{L.privacy}</Link>
          <span className="mx-2">·</span>
          <Link to="/" className="underline hover:text-zinc-900">{L.home}</Link>
        </div>
      </main>
    </div>
  );
}

const S = ({ children }) => <div className="text-zinc-700 space-y-6 text-[15px] leading-relaxed mt-8">{children}</div>;
const H = ({ children }) => <h2 className="text-2xl font-bold tracking-tight mt-10">{children}</h2>;

// Extract price helper: turn the backend's PLANS payload into a small object
// the body templates can interpolate. Currency symbol is mapped from ISO 4217
// so a backend switch to "eur" or "try" automatically updates the page.
function extract(data) {
  const symbols = { usd: "$", eur: "€", gbp: "£", try: "₺" };
  const plans = data?.plans || data || [];
  const find = (id) => plans.find(p => p.id === id) || {};
  const c = find("creator");
  const pr = find("pro");
  const cur = symbols[(c.currency || "usd").toLowerCase()] || "$";
  return {
    cur,
    creator: { m: c.monthly_price ?? 5, y: c.yearly_price ?? 50 },
    pro: { m: pr.monthly_price ?? 10, y: pr.yearly_price ?? 100 },
  };
}

// Format prices for inline use: e.g. $5/month or $50/year
const fmt = (p, plan) => `${p.cur}${p[plan].m}/month or ${p.cur}${p[plan].y}/year`;
const fmtTr = (p, plan) => `${p.cur}${p[plan].m}/ay veya ${p.cur}${p[plan].y}/yıl`;
const fmtEs = (p, plan) => `${p.cur}${p[plan].m}/mes o ${p.cur}${p[plan].y}/año`;
const fmtFr = (p, plan) => `${p.cur}${p[plan].m}/mois ou ${p.cur}${p[plan].y}/an`;
const fmtDe = (p, plan) => `${p.cur}${p[plan].m}/Monat oder ${p.cur}${p[plan].y}/Jahr`;
const fmtIt = (p, plan) => `${p.cur}${p[plan].m}/mese o ${p.cur}${p[plan].y}/anno`;

const TERMS_BODIES = {
  en: ({ p }) => (
    <S>
      <p>These Terms govern your access to and use of <strong>Lumioos</strong> (the "Service") on <strong>mylumioos.com</strong>. By creating an account or using the Service you agree to these Terms.</p>
      <H>1. Service</H>
      <p>Lumioos provides AI-powered tools: AI Chat, AI Video Producer (Sora 2), Interview Practice, and a project workspace.</p>
      <H>2. Eligibility</H>
      <p>You must be at least 13 years old (16 in the EU) and able to enter a binding contract.</p>
      <H>3. Accounts & Plans</H>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Free</strong>: 5 chat messages/day. Video & Interview locked.</li>
        <li><strong>Creator</strong>: {fmt(p, "creator")}. 15 videos/month, unlimited chat, full Interview access.</li>
        <li><strong>Pro</strong>: {fmt(p, "pro")}. 50 videos/month, HD 1080p, priority queue.</li>
        <li>New accounts receive a 2-day free Pro trial.</li>
      </ul>
      <H>4. Acceptable Use</H>
      <p>You may NOT use Lumioos to generate illegal content, deepfakes of real people, spam, harassment, or to abuse our infrastructure. We may suspend or terminate violating accounts without notice.</p>
      <H>5. AI-Generated Content</H>
      <p>AI outputs may be inaccurate, biased, or inappropriate. You are responsible for reviewing them. Do not rely on AI output for medical, legal, financial, or safety-critical decisions.</p>
      <H>6. Payments & Refunds</H>
      <p><strong>Refunds are NOT automatic.</strong> Subscriptions are billed in advance via Stripe. You can cancel anytime — your access remains until the end of the current billing period.</p>
      <p><strong>Refund requests are reviewed on a case-by-case basis at our sole discretion.</strong> We may grant a partial or full refund if you contact us within a reasonable time and explain the circumstances (e.g. accidental purchase, severe service outage on our side). We do NOT refund:</p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Time already passed on an active subscription.</li>
        <li>Accounts that have used paid features substantially (heavy AI video generation, many TTS calls, etc.).</li>
        <li>Accounts terminated for Terms violation.</li>
        <li>Requests made more than 14 days after the original charge.</li>
      </ul>
      <p>Refund requests: <a href="mailto:support@mylumioos.com" className="underline">support@mylumioos.com</a> — we respond within 5 business days.</p>
      <H>7. Intellectual Property</H>
      <p>Lumioos and its content (excluding user content and AI outputs) are owned by us and protected by intellectual property laws.</p>
      <H>8. Disclaimer of Warranties</H>
      <p>The Service is provided <strong>"AS IS"</strong>, without warranty of any kind.</p>
      <H>9. Limitation of Liability</H>
      <p>Our total liability is limited to the amount you paid us in the 12 months preceding the claim. We are not liable for indirect, incidental, or consequential damages.</p>
      <H>10. Termination</H>
      <p>You can delete your account at any time. We can suspend or terminate your account for violation. Upon termination your data is deleted within 30 days.</p>
      <H>11. Changes</H>
      <p>Material changes will be announced via email and on this page.</p>
      <H>12. Contact</H>
      <p>Questions or disputes: <a href="mailto:support@mylumioos.com" className="underline">support@mylumioos.com</a></p>
    </S>
  ),
  tr: ({ p }) => (
    <S>
      <p>Bu Koşullar, <strong>mylumioos.com</strong> üzerindeki <strong>Lumioos</strong> ("Hizmet") kullanımını düzenler. Hesap oluşturarak veya Hizmeti kullanarak bu koşulları kabul edersin.</p>
      <H>1. Hizmet</H>
      <p>Lumioos AI destekli araçlar sunar: AI Sohbet, AI Video Üretici (Sora 2), Mülakat Pratiği ve proje çalışma alanı.</p>
      <H>2. Uygunluk</H>
      <p>En az 13 yaşında (AB'de 16) olmalısın ve bağlayıcı sözleşme yapma yetkisine sahip olmalısın.</p>
      <H>3. Hesap ve Planlar</H>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Free</strong>: Günde 5 sohbet. Video ve Mülakat kilitli.</li>
        <li><strong>Creator</strong>: {fmtTr(p, "creator")}. Ayda 15 video, sınırsız sohbet, tam Mülakat erişimi.</li>
        <li><strong>Pro</strong>: {fmtTr(p, "pro")}. Ayda 50 video, HD 1080p, öncelikli kuyruk.</li>
        <li>Yeni hesaplar 2 günlük ücretsiz Pro denemesi alır.</li>
      </ul>
      <H>4. Kabul Edilebilir Kullanım</H>
      <p>Lumioos'u yasa dışı içerik, gerçek kişilerin deepfake'leri, spam, taciz veya altyapımızı kötüye kullanmak için KULLANAMAZSIN. İhlal eden hesapları önceden bildirim yapmadan askıya alabilir veya kapatabiliriz.</p>
      <H>5. AI Tarafından Üretilen İçerik</H>
      <p>AI çıktıları yanlış, ön yargılı veya uygunsuz olabilir. Gözden geçirmek senin sorumluluğundadır. Tıbbi, hukuki, finansal veya güvenlik kritik kararlar için AI çıktısına güvenme.</p>
      <H>6. Ödeme ve İade</H>
      <p><strong>İadeler otomatik DEĞİLDİR.</strong> Abonelikler Stripe üzerinden önceden faturalandırılır. İstediğin zaman iptal edebilirsin — erişimin mevcut faturalandırma dönemi sonuna kadar devam eder.</p>
      <p><strong>İade talepleri tamamen takdirimizde, vaka bazlı olarak incelenir.</strong> Bize makul bir süre içinde başvurur ve durumu açıklarsan (örneğin yanlışlıkla satın alma, bizim tarafımızdaki ciddi servis kesintisi) kısmi veya tam iade verebiliriz. Şunlar için iade YAPILMAZ:</p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Aktif abonelikte geçmiş süre.</li>
        <li>Ücretli özellikleri yoğun şekilde kullanan hesaplar (yoğun AI video üretimi, çok sayıda TTS çağrısı vb.).</li>
        <li>Koşulların ihlali nedeniyle kapatılan hesaplar.</li>
        <li>İlk ödemeden 14 günden fazla süre geçtikten sonra yapılan talepler.</li>
      </ul>
      <p>İade talepleri: <a href="mailto:support@mylumioos.com" className="underline">support@mylumioos.com</a> — 5 iş günü içinde yanıtlarız.</p>
      <H>7. Fikri Mülkiyet</H>
      <p>Lumioos ve içeriği (kullanıcı içeriği ve AI çıktıları hariç) bize aittir ve fikri mülkiyet yasaları ile korunur.</p>
      <H>8. Garanti Reddi</H>
      <p>Hizmet <strong>"OLDUĞU GİBİ"</strong> sunulur, hiçbir garanti verilmez.</p>
      <H>9. Sorumluluk Sınırı</H>
      <p>Toplam sorumluluğumuz, talep öncesindeki 12 ayda bize ödediğin tutarla sınırlıdır. Dolaylı veya sonuç olarak ortaya çıkan zararlardan sorumlu değiliz.</p>
      <H>10. Fesih</H>
      <p>Hesabını istediğin zaman silebilirsin. İhlal durumunda hesabını askıya alabilir veya kapatabiliriz. Fesih sonrası verilerin 30 gün içinde silinir.</p>
      <H>11. Değişiklikler</H>
      <p>Önemli değişiklikler e-posta ile ve bu sayfada duyurulur.</p>
      <H>12. İletişim</H>
      <p>Soru veya anlaşmazlık için: <a href="mailto:support@mylumioos.com" className="underline">support@mylumioos.com</a></p>
    </S>
  ),
  es: ({ p }) => (
    <S>
      <p>Estos Términos regulan el acceso y uso de <strong>Lumioos</strong> (el "Servicio") en <strong>mylumioos.com</strong>. Al crear una cuenta o usar el Servicio aceptas estos Términos.</p>
      <H>1. Servicio</H>
      <p>Lumioos ofrece herramientas con IA: Chat IA, Productor de Vídeo IA (Sora 2), Práctica de Entrevistas y espacio de proyecto.</p>
      <H>2. Elegibilidad</H>
      <p>Debes tener al menos 13 años (16 en la UE).</p>
      <H>3. Cuentas y Planes</H>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Free</strong>: 5 mensajes de chat/día. Vídeo y Entrevista bloqueados.</li>
        <li><strong>Creator</strong>: {fmtEs(p, "creator")}. 15 vídeos/mes, chat ilimitado, acceso completo a Entrevista.</li>
        <li><strong>Pro</strong>: {fmtEs(p, "pro")}. 50 vídeos/mes, HD 1080p, cola prioritaria.</li>
        <li>Nuevas cuentas reciben prueba gratuita Pro de 2 días.</li>
      </ul>
      <H>4. Uso Aceptable</H>
      <p>NO puedes usar Lumioos para contenido ilegal, deepfakes sin consentimiento, spam, acoso o abuso de infraestructura. Podemos suspender cuentas infractoras sin aviso.</p>
      <H>5. Contenido Generado por IA</H>
      <p>Las salidas de IA pueden ser inexactas, sesgadas o inapropiadas. Eres responsable de revisarlas. No confíes en la IA para decisiones médicas, legales, financieras o de seguridad crítica.</p>
      <H>6. Pagos y Reembolsos</H>
      <p><strong>Los reembolsos NO son automáticos.</strong> Las suscripciones se cobran por adelantado mediante Stripe. Puedes cancelar en cualquier momento — el acceso continúa hasta el final del período de facturación actual.</p>
      <p><strong>Las solicitudes de reembolso se revisan caso por caso a nuestra entera discreción.</strong> Podemos otorgar reembolso parcial o total si nos contactas en un plazo razonable explicando las circunstancias. NO reembolsamos:</p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Tiempo ya transcurrido en una suscripción activa.</li>
        <li>Cuentas que han usado funciones de pago de manera sustancial (mucha generación de vídeo IA, muchas llamadas TTS, etc.).</li>
        <li>Cuentas terminadas por violación de Términos.</li>
        <li>Solicitudes hechas más de 14 días después del cargo original.</li>
      </ul>
      <p>Solicitudes: <a href="mailto:support@mylumioos.com" className="underline">support@mylumioos.com</a> — respondemos en 5 días hábiles.</p>
      <H>7. Propiedad Intelectual</H>
      <p>Lumioos y su contenido (excluyendo contenido del usuario y salidas de IA) son nuestros y están protegidos por leyes de propiedad intelectual.</p>
      <H>8. Exención de Garantías</H>
      <p>El Servicio se proporciona <strong>"TAL CUAL"</strong>, sin garantías de ningún tipo.</p>
      <H>9. Limitación de Responsabilidad</H>
      <p>Nuestra responsabilidad total se limita al monto que nos pagaste en los 12 meses anteriores al reclamo.</p>
      <H>10. Terminación</H>
      <p>Puedes eliminar tu cuenta en cualquier momento. Podemos suspender o terminar tu cuenta por violación. Tras la terminación tus datos se eliminan en 30 días.</p>
      <H>11. Cambios</H>
      <p>Los cambios materiales se anunciarán por email y en esta página.</p>
      <H>12. Contacto</H>
      <p>Preguntas o disputas: <a href="mailto:support@mylumioos.com" className="underline">support@mylumioos.com</a></p>
    </S>
  ),
  fr: ({ p }) => (
    <S>
      <p>Ces Conditions régissent l'accès et l'utilisation de <strong>Lumioos</strong> (le "Service") sur <strong>mylumioos.com</strong>. En créant un compte ou en utilisant le Service tu acceptes ces Conditions.</p>
      <H>1. Service</H>
      <p>Lumioos fournit des outils IA: Chat IA, Producteur Vidéo IA (Sora 2), Pratique d'Entretien et espace de projet.</p>
      <H>2. Éligibilité</H>
      <p>Tu dois avoir au moins 13 ans (16 dans l'UE).</p>
      <H>3. Comptes et Plans</H>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Free</strong>: 5 messages/jour. Vidéo et Entretien verrouillés.</li>
        <li><strong>Creator</strong>: {fmtFr(p, "creator")}. 15 vidéos/mois, chat illimité, accès complet à Entretien.</li>
        <li><strong>Pro</strong>: {fmtFr(p, "pro")}. 50 vidéos/mois, HD 1080p, file prioritaire.</li>
        <li>Les nouveaux comptes reçoivent un essai Pro gratuit de 2 jours.</li>
      </ul>
      <H>4. Utilisation Acceptable</H>
      <p>Tu ne peux PAS utiliser Lumioos pour du contenu illégal, des deepfakes sans consentement, du spam, du harcèlement ou pour abuser de notre infrastructure. Nous pouvons suspendre les comptes en infraction sans préavis.</p>
      <H>5. Contenu Généré par IA</H>
      <p>Les sorties IA peuvent être inexactes, biaisées ou inappropriées. Tu es responsable de les vérifier. Ne te fie pas à l'IA pour des décisions médicales, juridiques, financières ou de sécurité critique.</p>
      <H>6. Paiements et Remboursements</H>
      <p><strong>Les remboursements NE sont PAS automatiques.</strong> Les abonnements sont facturés à l'avance via Stripe. Tu peux annuler à tout moment — l'accès continue jusqu'à la fin de la période de facturation actuelle.</p>
      <p><strong>Les demandes de remboursement sont examinées au cas par cas à notre seule discrétion.</strong> Nous pouvons accorder un remboursement partiel ou total si tu nous contactes dans un délai raisonnable. Nous ne remboursons PAS:</p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Le temps déjà écoulé sur un abonnement actif.</li>
        <li>Les comptes ayant utilisé les fonctionnalités payantes de manière substantielle (génération vidéo IA intensive, nombreux appels TTS, etc.).</li>
        <li>Les comptes résiliés pour violation des Conditions.</li>
        <li>Les demandes faites plus de 14 jours après le débit initial.</li>
      </ul>
      <p>Demandes: <a href="mailto:support@mylumioos.com" className="underline">support@mylumioos.com</a> — réponse sous 5 jours ouvrés.</p>
      <H>7. Propriété Intellectuelle</H>
      <p>Lumioos et son contenu nous appartiennent et sont protégés par les lois sur la propriété intellectuelle.</p>
      <H>8. Exclusion de Garanties</H>
      <p>Le Service est fourni <strong>"EN L'ÉTAT"</strong>, sans garantie d'aucune sorte.</p>
      <H>9. Limitation de Responsabilité</H>
      <p>Notre responsabilité totale est limitée au montant que tu nous as payé dans les 12 mois précédant la réclamation.</p>
      <H>10. Résiliation</H>
      <p>Tu peux supprimer ton compte à tout moment. Nous pouvons suspendre ou résilier ton compte pour violation. Après résiliation, tes données sont supprimées sous 30 jours.</p>
      <H>11. Modifications</H>
      <p>Les changements importants seront annoncés par email et sur cette page.</p>
      <H>12. Contact</H>
      <p>Questions ou litiges: <a href="mailto:support@mylumioos.com" className="underline">support@mylumioos.com</a></p>
    </S>
  ),
  de: ({ p }) => (
    <S>
      <p>Diese Bedingungen regeln den Zugang zu und die Nutzung von <strong>Lumioos</strong> (der "Dienst") auf <strong>mylumioos.com</strong>. Mit der Erstellung eines Kontos oder Nutzung des Dienstes akzeptierst du diese Bedingungen.</p>
      <H>1. Dienst</H>
      <p>Lumioos bietet KI-Tools: KI-Chat, KI-Video-Produzent (Sora 2), Interview-Praxis und Projekt-Arbeitsbereich.</p>
      <H>2. Berechtigung</H>
      <p>Du musst mindestens 13 Jahre alt sein (16 in der EU).</p>
      <H>3. Konten und Pläne</H>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Free</strong>: 5 Chat-Nachrichten/Tag. Video und Interview gesperrt.</li>
        <li><strong>Creator</strong>: {fmtDe(p, "creator")}. 15 Videos/Monat, unbegrenzter Chat, voller Interview-Zugang.</li>
        <li><strong>Pro</strong>: {fmtDe(p, "pro")}. 50 Videos/Monat, HD 1080p, prioritäre Warteschlange.</li>
        <li>Neue Konten erhalten 2 Tage kostenlose Pro-Testversion.</li>
      </ul>
      <H>4. Akzeptable Nutzung</H>
      <p>Du darfst Lumioos NICHT für illegale Inhalte, Deepfakes echter Personen, Spam, Belästigung oder Missbrauch unserer Infrastruktur verwenden. Wir können Konten ohne Vorwarnung sperren oder kündigen.</p>
      <H>5. KI-generierte Inhalte</H>
      <p>KI-Ausgaben können ungenau, voreingenommen oder unangemessen sein. Du bist verantwortlich für deren Überprüfung. Vertraue nicht auf KI für medizinische, rechtliche, finanzielle oder sicherheitskritische Entscheidungen.</p>
      <H>6. Zahlungen und Rückerstattungen</H>
      <p><strong>Rückerstattungen erfolgen NICHT automatisch.</strong> Abonnements werden im Voraus über Stripe abgerechnet. Du kannst jederzeit kündigen — der Zugang bleibt bis zum Ende des aktuellen Abrechnungszeitraums bestehen.</p>
      <p><strong>Rückerstattungsanfragen werden im Einzelfall nach unserem alleinigen Ermessen geprüft.</strong> Wir können eine teilweise oder vollständige Rückerstattung gewähren, wenn du uns innerhalb einer angemessenen Frist kontaktierst. Wir erstatten NICHT:</p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Bereits abgelaufene Zeit eines aktiven Abonnements.</li>
        <li>Konten, die kostenpflichtige Funktionen substantiell genutzt haben (umfangreiche KI-Video-Generierung, viele TTS-Aufrufe usw.).</li>
        <li>Wegen Verletzung der Bedingungen gekündigte Konten.</li>
        <li>Anträge, die mehr als 14 Tage nach der ursprünglichen Belastung gestellt wurden.</li>
      </ul>
      <p>Anfragen: <a href="mailto:support@mylumioos.com" className="underline">support@mylumioos.com</a> — Antwort innerhalb von 5 Werktagen.</p>
      <H>7. Geistiges Eigentum</H>
      <p>Lumioos und seine Inhalte sind unser Eigentum und durch Urheberrechtsgesetze geschützt.</p>
      <H>8. Gewährleistungsausschluss</H>
      <p>Der Dienst wird <strong>"WIE BESEHEN"</strong> bereitgestellt, ohne jegliche Gewährleistung.</p>
      <H>9. Haftungsbeschränkung</H>
      <p>Unsere Gesamthaftung ist auf den Betrag begrenzt, den du uns in den 12 Monaten vor dem Anspruch gezahlt hast.</p>
      <H>10. Kündigung</H>
      <p>Du kannst dein Konto jederzeit löschen. Wir können dein Konto bei Verletzung sperren oder kündigen. Nach Kündigung werden deine Daten innerhalb von 30 Tagen gelöscht.</p>
      <H>11. Änderungen</H>
      <p>Wesentliche Änderungen werden per E-Mail und auf dieser Seite angekündigt.</p>
      <H>12. Kontakt</H>
      <p>Fragen oder Streitigkeiten: <a href="mailto:support@mylumioos.com" className="underline">support@mylumioos.com</a></p>
    </S>
  ),
  it: ({ p }) => (
    <S>
      <p>Questi Termini regolano l'accesso e l'uso di <strong>Lumioos</strong> (il "Servizio") su <strong>mylumioos.com</strong>. Creando un account o usando il Servizio accetti questi Termini.</p>
      <H>1. Servizio</H>
      <p>Lumioos fornisce strumenti IA: Chat IA, Produttore Video IA (Sora 2), Pratica Colloqui e spazio progetti.</p>
      <H>2. Idoneità</H>
      <p>Devi avere almeno 13 anni (16 nell'UE).</p>
      <H>3. Account e Piani</H>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Free</strong>: 5 messaggi/giorno. Video e Colloquio bloccati.</li>
        <li><strong>Creator</strong>: {fmtIt(p, "creator")}. 15 video/mese, chat illimitata, accesso completo Colloquio.</li>
        <li><strong>Pro</strong>: {fmtIt(p, "pro")}. 50 video/mese, HD 1080p, coda prioritaria.</li>
        <li>I nuovi account ricevono prova Pro gratuita di 2 giorni.</li>
      </ul>
      <H>4. Uso Accettabile</H>
      <p>NON puoi usare Lumioos per contenuti illegali, deepfake senza consenso, spam, molestie o abuso dell'infrastruttura. Possiamo sospendere account in violazione senza preavviso.</p>
      <H>5. Contenuti Generati dall'IA</H>
      <p>Le uscite IA possono essere inaccurate, di parte o inappropriate. Sei responsabile della loro revisione. Non fare affidamento sull'IA per decisioni mediche, legali, finanziarie o di sicurezza critica.</p>
      <H>6. Pagamenti e Rimborsi</H>
      <p><strong>I rimborsi NON sono automatici.</strong> Gli abbonamenti vengono fatturati in anticipo tramite Stripe. Puoi annullare in qualsiasi momento — l'accesso continua fino alla fine del periodo di fatturazione corrente.</p>
      <p><strong>Le richieste di rimborso sono valutate caso per caso a nostra esclusiva discrezione.</strong> Possiamo concedere un rimborso parziale o totale se ci contatti entro un tempo ragionevole. NON rimborsiamo:</p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Tempo già trascorso su un abbonamento attivo.</li>
        <li>Account che hanno utilizzato funzionalità a pagamento in modo sostanziale (forte generazione video IA, molte chiamate TTS, ecc.).</li>
        <li>Account chiusi per violazione dei Termini.</li>
        <li>Richieste fatte più di 14 giorni dopo l'addebito originale.</li>
      </ul>
      <p>Richieste: <a href="mailto:support@mylumioos.com" className="underline">support@mylumioos.com</a> — rispondiamo entro 5 giorni lavorativi.</p>
      <H>7. Proprietà Intellettuale</H>
      <p>Lumioos e i suoi contenuti ci appartengono e sono protetti dalle leggi sulla proprietà intellettuale.</p>
      <H>8. Esclusione di Garanzie</H>
      <p>Il Servizio è fornito <strong>"COSÌ COM'È"</strong>, senza garanzie di alcun tipo.</p>
      <H>9. Limitazione di Responsabilità</H>
      <p>La nostra responsabilità totale è limitata all'importo che ci hai pagato nei 12 mesi precedenti la richiesta.</p>
      <H>10. Cessazione</H>
      <p>Puoi eliminare il tuo account in qualsiasi momento. Possiamo sospendere o terminare il tuo account per violazione. Dopo la cessazione i tuoi dati vengono eliminati entro 30 giorni.</p>
      <H>11. Modifiche</H>
      <p>Le modifiche sostanziali saranno annunciate via email e su questa pagina.</p>
      <H>12. Contatto</H>
      <p>Domande o controversie: <a href="mailto:support@mylumioos.com" className="underline">support@mylumioos.com</a></p>
    </S>
  ),
};
;
